import { Fingerprint, Layers3, ShieldCheck, Tags } from "lucide-react";

import { BrokerArtwork } from "@web/features/terminal/BrokerArtwork";

const samples = [
  { tokenId: 1, family: "Grounded", count: "3,111", share: "70%" },
  { tokenId: 2, family: "Wall Street", count: "889", share: "20%" },
  { tokenId: 7, family: "Strange Desk", count: "355", share: "8%" },
  { tokenId: 42, family: "Grail", count: "89", share: "2%" },
];

const traitGroups = [
  { label: "Scene", traits: "Background · Foreground Effect" },
  {
    label: "Identity",
    traits: "Foundation · Eyes · Hair · Face Detail",
  },
  {
    label: "Wardrobe",
    traits: "Outfit · Headwear · Eyewear · Jewelry · Face Covering",
  },
  { label: "Desk", traits: "Prop" },
  { label: "Special", traits: "Tier · Grail Scene · Grade" },
];

export function CollectionPage() {
  return (
    <main className="collection-page">
      <section className="collection-intro">
        <div>
          <span className="section-kicker">The Broker collection</span>
          <h1>4,444 portfolios with a face.</h1>
          <p>
            Every Broker is original artwork with its own built-in wallet.
            That wallet holds the RWA tokens it earns and moves with the NFT.
          </p>
        </div>
        <dl className="collection-facts">
          <div>
            <dt>Editions</dt>
            <dd>4,444</dd>
          </div>
          <div>
            <dt>Source assets</dt>
            <dd>260</dd>
          </div>
          <div>
            <dt>Trait types</dt>
            <dd>15</dd>
          </div>
          <div>
            <dt>Format</dt>
            <dd>1024² WebP</dd>
          </div>
        </dl>
      </section>

      <section
        className="collection-showcase"
        aria-label="Broker art families"
      >
        {samples.map((sample) => (
          <article key={sample.tokenId}>
            <BrokerArtwork tokenId={sample.tokenId} />
            <div>
              <span>
                Broker #{String(sample.tokenId).padStart(4, "0")}
              </span>
              <strong>{sample.family}</strong>
              <small>
                {sample.count} editions · {sample.share}
              </small>
            </div>
          </article>
        ))}
      </section>

      <section className="collection-content">
        <div className="collection-section-heading">
          <span className="section-kicker">Art system</span>
          <h2>Made from approved layers. Built as unique editions.</h2>
          <p>
            Every Broker is built from a hand-picked set of traits. No two
            Brokers use the same combination.
          </p>
        </div>

        <div className="collection-principles">
          <article>
            <Layers3 size={20} />
            <h3>Four visual families</h3>
            <p>
              Grounded internet brokers form the base, followed by Wall Street
              villains, strange desks, and fully composed grail scenes.
            </p>
          </article>
          <article>
            <Tags size={20} />
            <h3>Readable metadata</h3>
            <p>
              Each JSON record includes its family, visual attributes, DNA,
              rarity score, and the collection frequency of every trait.
            </p>
          </article>
          <article>
            <Fingerprint size={20} />
            <h3>Verifiable output</h3>
            <p>
              Every approved source layer and final image is covered by the
              published commitments. Metadata files are independently hashed
              in the collection manifest.
            </p>
          </article>
        </div>

        <section className="trait-section">
          <div className="collection-section-heading">
            <span className="section-kicker">Traits</span>
            <h2>What appears in the metadata</h2>
          </div>
          <div className="trait-list">
            {traitGroups.map((group) => (
              <div key={group.label}>
                <strong>{group.label}</strong>
                <span>{group.traits}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="provenance-section">
          <ShieldCheck size={22} />
          <div>
            <span className="section-kicker">Provenance</span>
            <h2>The art release is committed before deployment.</h2>
            <p>
              The canonical renderer resolves each token to a four-digit JSON
              record. The same release supplies the image and metadata used by
              wallets, marketplaces, the API, and this website.
            </p>
            <dl>
              <div>
                <dt>Collection SHA-256</dt>
                <dd>
                  c94ea9a572f214e49d7cc4a3c6ee1ae51ecf285f261624dd9a3e1ea66019f306
                </dd>
              </div>
              <div>
                <dt>Source SHA-256</dt>
                <dd>
                  36dfaabfc60441da1e6cff45148bbcec51f272b4ce816b510b357c8649ff2095
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </section>
    </main>
  );
}
